import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { socialChannels } from 'common/ShareSheetV2/Screens/Default/Default'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import dayjs from 'dayjs'
import { useCalendarTargetAnalysis } from 'hooks/useCalendarTargetAnalysis'

import { useMemo } from 'react'
import { AlmanacFileCalendarEvent } from 'screens/Calendar/types'
import { getCrossPostGroupForChannel } from 'screens/Calendar/FullScreenPreview/utils'
import { Workspace } from 'types'
import { Props as CalendarStatusPillProps } from 'components/CalendarStatusPill/CalendarStatusPill'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

type Input = {
  connectedBlogIntegration: ExternalIntegration | null
  connectedEmailIntegration?: ExternalIntegration | null
  event: Pick<
    AlmanacFileCalendarEvent,
    | 'asset_type'
    | 'created_at'
    | 'channel'
    | 'status'
    | 'scheduled_post'
    | 'did_generation_fail'
    | 'image_url_generating'
    | 'image_url'
    | 'reviewed_at'
    | 'viewed_at'
    | 'scheduled_post_targets'
    | 'external_integration_id'
    | 'post_at'
  >
  validationErrors: string[]
  workspace: Workspace | undefined
}

// Priority order (highest to lowest):
// failed > posted > updating > new > warning > needsConnect > needsApproval > approved > paused > draft
// Note: needsConnect beats needsApproval — can't review a post that can't be published.
// isApproved beats isNew (new is suppressed when approved).
// When did_generation_fail is true (and not posted), only 'draft' can be the status.
type DisplayStatus =
  | 'failed'
  | 'posted'
  | 'updating'
  | 'needsApproval'
  | 'new'
  | 'warning'
  | 'needsConnect'
  | 'approved'
  | 'paused'
  | 'draft'
  | null

type Output = {
  approvalsEnabled: boolean
  didGenerationFail: boolean
  hasUnconnectedTargets: boolean
  isApproved: boolean
  isDraft: boolean
  isFailed: boolean
  isHybrid: boolean
  isNew: boolean
  isPosted: boolean
  isUpdating: boolean
  isWarning: boolean
  isPaused: boolean
  mobileStatus: CalendarStatusPillProps['status'] | null
  needsApproval: boolean
  needsConnect: boolean
  targetAnalysis: ReturnType<typeof useCalendarTargetAnalysis>
  unconnectedTargetCount: number
}

export const useStatus = (input: Input): Output => {
  const { event, validationErrors, workspace, connectedBlogIntegration, connectedEmailIntegration = null } = input
  const {
    asset_type,
    created_at,
    channel,
    status,
    scheduled_post,
    did_generation_fail,
    image_url_generating,
    image_url,
    reviewed_at,
    viewed_at,
    scheduled_post_targets,
    external_integration_id,
    post_at,
  } = event

  const approvalsEnabled = workspace?.post_approval_enabled
  const isCopilot = !workspace?.is_virtual_marketer
  const targetAnalysis = useCalendarTargetAnalysis(event)
  const { data: integrationsData } = useExternalIntegrations()

  const hasUnconnectedTargetsForVirtualMarketer = socialChannels.includes(channel as any)
    ? !scheduled_post_targets?.length || !!scheduled_post_targets?.some((target) => !target.external_integration_id)
    : !external_integration_id
  const hasUnconnectedTargets = isCopilot
    ? !scheduled_post?.external_integration
    : hasUnconnectedTargetsForVirtualMarketer

  const unconnectedTargetCount = useMemo(() => {
    if (!socialChannels.includes(channel as any)) return 0
    const crossPostGroup = getCrossPostGroupForChannel(channel ?? undefined)
    const connectedProviders = new Set(integrationsData?.external_integrations?.map((i) => i.provider) ?? [])
    return crossPostGroup.filter((p) => !connectedProviders.has(p)).length
  }, [channel, integrationsData])

  const displayStatus = resolveDisplayStatus({
    targetAnalysis,
    status,
    did_generation_fail,
    image_url_generating,
    image_url,
    approvalsEnabled,
    reviewed_at,
    created_at,
    viewed_at,
    asset_type,
    validationErrors,
    scheduled_post,
    hasUnconnectedTargets,
    channel,
    connectedBlogIntegration,
    connectedEmailIntegration,
    pause_autopilot_publish_at: workspace?.pause_autopilot_publish_at,
    post_at,
  })

  const isPosted =
    displayStatus === 'posted' || !!event?.scheduled_post_targets?.some((target) => target.status === 'published')
  const isFailed =
    !isPosted &&
    (displayStatus === 'failed' || !!event?.scheduled_post_targets?.some((target) => target.status === 'failed'))
  const isHybrid = isPosted && !targetAnalysis.useOldLogic && targetAnalysis.isHybrid
  const isUpdating = displayStatus === 'updating'
  const needsApproval = displayStatus === 'needsApproval'
  const isApproved = displayStatus === 'approved'
  const isPaused = displayStatus === 'paused'
  const isNew = displayStatus === 'new'
  const isWarning = displayStatus === 'warning'
  const needsConnect = displayStatus === 'needsConnect'
  const isDraft = displayStatus === 'draft'
  const didGenerationFail = !!(did_generation_fail && !isPosted)

  const mobileStatus = useMemo<CalendarStatusPillProps['status'] | null>(() => {
    if (isFailed) return 'Failed'
    if (isPosted) return isHybrid ? 'Posted-Failed' : 'Posted'
    if (needsConnect) return 'Connect'
    if (needsApproval) return 'Review'
    if (isApproved) return 'Approved'
    if (isPaused) return 'Paused'
    if (isDraft) return 'Draft'
    if (isNew) return 'New'
    if (post_at) {
      if (isHybrid) return 'Scheduled-Alert'
      return 'Scheduled'
    }
    return null
  }, [isFailed, isPosted, needsConnect, needsApproval, isApproved, isDraft, isHybrid, post_at, isNew, isPaused])

  return {
    approvalsEnabled: !!approvalsEnabled,
    didGenerationFail,
    hasUnconnectedTargets,
    isApproved,
    isDraft,
    isFailed,
    isHybrid,
    isNew,
    isPosted,
    isUpdating,
    isWarning,
    isPaused,
    mobileStatus,
    needsApproval,
    needsConnect,
    targetAnalysis,
    unconnectedTargetCount,
  }
}

function resolveDisplayStatus(ctx: {
  targetAnalysis: ReturnType<typeof useCalendarTargetAnalysis>
  status: string | null | undefined
  did_generation_fail: boolean | null | undefined
  image_url_generating: boolean | null | undefined
  image_url: string | null | undefined
  approvalsEnabled: boolean | null | undefined
  reviewed_at: string | null | undefined
  created_at: string
  viewed_at: string | null | undefined
  asset_type: string | null | undefined
  validationErrors: string[]
  scheduled_post: { error_message?: string } | null | undefined
  hasUnconnectedTargets: boolean
  channel: string | null | undefined
  connectedBlogIntegration: ExternalIntegration | null
  connectedEmailIntegration: ExternalIntegration | null | undefined
  pause_autopilot_publish_at: string | null | undefined
  post_at: string | null | undefined
}): DisplayStatus {
  const isPosted = ctx.targetAnalysis.useOldLogic
    ? ctx.status === PostStatusPropertyValue.Posted
    : ctx.targetAnalysis.isPureSuccess || ctx.targetAnalysis.isHybrid
  const isFailed = ctx.targetAnalysis.useOldLogic
    ? ctx.status === PostStatusPropertyValue.Failed
    : ctx.targetAnalysis.isPureFailure || ctx.status === PostStatusPropertyValue.Failed

  if (isFailed) return 'failed'
  if (isPosted) return 'posted'

  // Generation failure suppresses all statuses except draft
  if (ctx.did_generation_fail) {
    return ctx.status === PostStatusPropertyValue.Draft ? 'draft' : null
  }

  if (ctx.image_url_generating && ctx.image_url) return 'updating'

  // Compute approved early — isNew is suppressed when the post is already approved
  const isApproved = ctx.approvalsEnabled && !!ctx.post_at && ctx.reviewed_at

  if (isApproved) return 'approved'

  const isHybrid = !ctx.targetAnalysis.useOldLogic && ctx.targetAnalysis.isHybrid
  if (!isApproved && dayjs().diff(dayjs(ctx.created_at), 'day') < 1 && !ctx.viewed_at && !isHybrid) return 'new'

  if ((ctx.asset_type === 'Article' && ctx.validationErrors.length > 0) || ctx.scheduled_post?.error_message)
    return 'warning'

  if (!ctx.post_at) return 'draft'

  if (ctx.approvalsEnabled && !ctx.reviewed_at) return 'needsApproval'

  // needsConnect takes priority over needsApproval — can't review a post that can't be published
  const hasConnectedBlogIntegration = ctx.channel === ChannelType.BlogPost && ctx.connectedBlogIntegration
  const hasConnectedEmailIntegration = ctx.channel === ChannelType.Newsletter && ctx.connectedEmailIntegration
  if (ctx.hasUnconnectedTargets && !hasConnectedBlogIntegration && !hasConnectedEmailIntegration) return 'needsConnect'

  if (
    ctx.pause_autopilot_publish_at &&
    ctx.post_at &&
    dayjs(ctx.post_at).isAfter(dayjs(ctx.pause_autopilot_publish_at))
  )
    return 'paused'

  if (ctx.status === PostStatusPropertyValue.Draft) return 'draft'

  return null
}
